import {
  AttachFileRounded,
  CircleRounded,
  CloseRounded,
  InsertDriveFileOutlined,
  ReportProblemRounded,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useFeedback } from "../../../../providers/feedback/feeedback";
import { black, greyEnabled, purple, purpleLight, red, redLight } from "../../../../theme/colors";

export default function UpdateNeedStatus({
  open,
  mode = "close",
  onDialogClose,
  onSubmit,
  submitting = false,
}: {
  open: boolean;
  mode?: "close" | "resolve";
  onDialogClose: () => void;
  onSubmit: (remarks: string, file: File | null, notificationMode: string[]) => void;
  submitting?: boolean;
}) {
  const isResolve = mode === "resolve";
  const [comments, setComments] = React.useState("");
  const [error, setError] = React.useState("");
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const { showSnackbar } = useFeedback()
  const [notificationSentMode, setNotificationSentMode] = React.useState<string[]>([]);

  const handleClose = () => !submitting && onDialogClose();

  function submit() {
    setError("");
    if (comments.length === 0) {
      setError(isResolve ? "Enter resolution remarks" : "Enter comments");
    } else {
      onSubmit(comments, selectedFile, notificationSentMode);
    }
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    const maxFileSize = 10 * 1024 * 1024;
    if (file != null) {
      if (file.size > maxFileSize) {
        showSnackbar("File size exceeds 10 MB");
      } else {
        setSelectedFile(file)
      }

    }
  };

  const handleRadioChange = (val: string) => {
    if (notificationSentMode.includes(val)) {
      let filteredData = notificationSentMode.filter((i) => i !== val)
      setNotificationSentMode(filteredData)
    } else {
      setNotificationSentMode([...notificationSentMode, val])
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} PaperProps={{ sx: { borderRadius: 4 } }}>
      <Box sx={{ width: 460, maxWidth: "90vw" }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: "1px solid #ECECEC" }}
        >
          <Typography variant="h6" fontWeight={700} color={black}>
            {isResolve ? "Mark Need as Resolved" : "Mark Need as Closed"}
          </Typography>
          <IconButton onClick={handleClose} size="small" disabled={submitting}>
            <CloseRounded sx={{ color: greyEnabled }} />
          </IconButton>
        </Box>

        <Box sx={{ px: 3, pt: 2.5, pb: 3 }}>
          <Box
            display="flex"
            alignItems="flex-start"
            gap={1.5}
            sx={{ bgcolor: redLight, borderRadius: 2, p: 1.5, mb: 2.5 }}
          >
            <ReportProblemRounded sx={{ color: red, fontSize: 20, mt: 0.25 }} />
            <Typography variant="body2" color={black}>
              {isResolve
                ? "Are you sure you want to mark this need as resolved?"
                : "Are you sure you want to mark this need as closed? This action cannot be undone."}
            </Typography>
          </Box>

          <Box position="relative">
            <TextField
              multiline
              minRows={5}
              fullWidth
              disabled={submitting}
              value={comments}
              onChange={({ target: { value } }) => {
                setComments(value);
              }}
              error={Boolean(error)}
              helperText={error || `${300 - comments.length} characters left`}
              label={isResolve ? "Resolution Remarks" : "Closing Feedback"}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                maxLength: 300,
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#ABA0AB" },
                  "&:hover fieldset": { borderColor: purple },
                  "&.Mui-focused fieldset": { borderColor: purple, borderWidth: 2 },
                },
              }}
            />
          </Box>

          {!selectedFile ? (
            <Box
              mt={2}
              p={1.5}
              display="flex"
              alignItems="center"
              gap={1}
              sx={{ border: "1px dashed #ABA0AB", borderRadius: 2 }}
            >
              <label htmlFor="fileUpload">
                <IconButton component="span" disabled={submitting} size="small">
                  <AttachFileRounded sx={{ color: purple }} />
                </IconButton>
              </label>
              <input
                id="fileUpload"
                onChange={handleFileChange}
                type="file"
                style={{ display: "none" }}
                accept=".png, .jpg, .jpeg, .mp4, .mp3"
                disabled={submitting}
              />
              <label htmlFor="fileUpload">
                <Button component="span" disabled={submitting} sx={{ color: purple, textTransform: "none", fontWeight: 600 }}>
                  Click here to upload image, video or audio
                </Button>
              </label>
            </Box>
          ) : (
            <Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              gap={1}
              mt={2}
              p={1.5}
              sx={{ border: "1px solid #ECECEC", borderRadius: 2, bgcolor: purpleLight }}
            >
              <InsertDriveFileOutlined sx={{ color: purple }} />
              <Typography flex={1} fontWeight={600} color={black} noWrap>
                {selectedFile?.name}
              </Typography>
              <IconButton size="small" onClick={() => setSelectedFile(null)} disabled={submitting}>
                <CloseRounded sx={{ fontSize: 18, color: greyEnabled }} />
              </IconButton>
            </Box>
          )}
          <Typography variant="caption" color={greyEnabled} display="block" mt={1}>
            Upload a .png, .jpeg, .mp4 or .mp3 file with a maximum size of 10 MB
          </Typography>

          {/* SMS/WhatsApp notification options hidden - the backend
              (needs-status-notify.js) doesn't actually send either one yet;
              sendSMS/sendWhatsApp and their invocation are commented out
              there. Re-enable this section once that's implemented, so
              admins aren't offered an option that silently does nothing.
          <Typography variant="body1" fontWeight={600} color="primary.main" mt={3} mb={1}>
            Notification Option
          </Typography>

          <Box display="flex" flexDirection="row" gap={2}>
            <Box display="flex" alignItems="center">
              <Checkbox
                checked={notificationSentMode.includes('sms')}
                name="sms"
                disabled={submitting}
                onChange={() => handleRadioChange('sms')}
                icon={<CircleRounded sx={{ color: "#ABA0AB", fontSize: 20 }} />}
                checkedIcon={<CircleRounded sx={{ color: purple, fontSize: 20 }} />}
              />
              <Typography variant="body2" fontWeight={600}>
                SMS
              </Typography>
            </Box>
            <Box display="flex" alignItems="center">
              <Checkbox
                checked={notificationSentMode.includes('whatsapp')}
                name="whatsapp"
                disabled={submitting}
                onChange={() => handleRadioChange('whatsapp')}
                icon={<CircleRounded sx={{ color: "#ABA0AB", fontSize: 20 }} />}
                checkedIcon={<CircleRounded sx={{ color: purple, fontSize: 20 }} />}
              />
              <Typography variant="body2" fontWeight={600}>
                Whatsapp
              </Typography>
            </Box>
          </Box>
          */}

          <Box display="flex" gap={1.5} mt={4}>
            <Button
              fullWidth
              disabled={submitting}
              onClick={handleClose}
              variant="outlined"
              sx={{
                borderColor: purple,
                color: purple,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { borderColor: purple, bgcolor: purpleLight },
              }}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              variant="contained"
              disableElevation
              onClick={submit}
              disabled={!comments || submitting}
              startIcon={submitting ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : undefined}
              sx={{
                bgcolor: purple,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { bgcolor: "#7A2074" },
              }}
            >
              {isResolve ? "Mark as Resolved" : "Mark as Closed"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}
